import { Fragment, useEffect, useMemo, type ReactNode } from 'react';
import { z } from 'zod';
import en from 'zod/v4/locales/en.js';
import ko from 'zod/v4/locales/ko.js';
import vi from 'zod/v4/locales/vi.js';
import zhCN from 'zod/v4/locales/zh-CN.js';
import zhTW from 'zod/v4/locales/zh-TW.js';
import { UiStringsProvider, type UiStrings } from '@aitoken/ui/lib/ui-strings';
import type { Locale } from '@aitoken/contracts';
import { useLocale } from './index';
import { commonT } from './messages/common';
import commonZh from './messages/common/zh-CN';
import { onLocaleChange } from './runtime';

const ZOD: Record<Locale, () => Parameters<typeof z.config>[0]> = {
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  en,
  vi,
  ko,
};

// 表单校验提示跟随界面语言（复用 contracts 中的 zod schema 时同样生效）
onLocaleChange((l) => z.config(ZOD[l]()));

const STATUS_KEYS = new Set<string>(Object.keys(commonZh).filter((k) => k.startsWith('status.')));

function uiStrings(): UiStrings {
  const t = commonT as unknown as (k: string, p?: Record<string, string>) => string;
  return {
    close: commonT('close'),
    copied: commonT('copied'),
    copyValue: (value) => commonT('copyValue', { value }),
    toggleSidebar: commonT('toggleSidebar'),
    succeeded: commonT('succeeded'),
    failed: commonT('failed'),
    timelineSameId: commonT('timelineSameId'),
    status: (s) => {
      const key = `status.${s}`;
      return STATUS_KEYS.has(key) ? t(key) : undefined;
    },
    site: (site) => commonT(site === 'cn' ? 'site.cn' : 'site.global'),
  };
}

/**
 * 语言边界：切换语言时以 key 重挂载整棵界面，保证所有文案（含非 hook 的格式化函数输出）同步刷新；
 * 路由状态与查询缓存在外部对象里，不会丢失。
 */
export function I18nBoundary({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const strings = useMemo(() => uiStrings(), [locale]);
  useEffect(() => {
    z.config(ZOD[locale]());
  }, [locale]);
  return (
    <UiStringsProvider value={strings}>
      <Fragment key={locale}>{children}</Fragment>
    </UiStringsProvider>
  );
}
